import { DATA, type ClassId } from '../types.js';
import type { StatBonuses } from './attributes.js';

/**
 * feature 4.1 — the XP → level curve, shared by the sim (level-up detection in
 * `World`) and the client HUD (the XP progress bar). Reads `DATA.progression`.
 *
 * The cumulative XP required to *reach* level `L` (L >= 1, level 1 = 0 XP) is
 *   `xpToReach(L) = sum over k=2..L of (xpBase + (k - 2) * xpGrowth)`
 * i.e. the per-level increment grows linearly: `xpBase`, `xpBase + xpGrowth`, …
 */

/** Cumulative XP needed to reach `level`. `xpToReach(1) === 0`; `level` is
 * floored and clamped to `>= 1`. Not clamped at the cap — a hypothetical
 * `xpToReach(levelCap + 1)` is still well defined (used only internally). */
export function xpToReach(level: number): number {
  const { xpBase, xpGrowth } = DATA.progression;
  const target = Math.max(1, Math.floor(level));
  let total = 0;
  for (let k = 2; k <= target; k++) total += xpBase + (k - 2) * xpGrowth;
  return total;
}

/**
 * The XP span of `level` → `level + 1` (`xpToReach(level + 1) - xpToReach(level)`),
 * for the HUD progress bar. `0` at (and past) the level cap so the bar reads
 * full there.
 */
export function xpToNext(level: number): number {
  const { levelCap } = DATA.progression;
  const l = Math.max(1, Math.floor(level));
  if (l >= levelCap) return 0;
  return xpToReach(l + 1) - xpToReach(l);
}

/**
 * The highest level whose `xpToReach` is `<= xp`, clamped to `[1, levelCap]`.
 * XP past `xpToReach(levelCap)` still returns `levelCap` (criterion 3).
 *
 * `from` is an optional lower bound — a player's current level — so the sim's
 * per-tick check walks up only a step or two instead of from 1. Correctness does
 * not depend on it: `xp` only ever increases and `level` only ever follows it
 * up, so `from <= levelForXp(xp)` always holds in practice.
 */
export function levelForXp(xp: number, from = 1): number {
  const { levelCap } = DATA.progression;
  let level = Math.max(1, Math.min(levelCap, Math.floor(from)));
  while (level < levelCap && xpToReach(level + 1) <= xp) level++;
  return level;
}

/**
 * feature 4.1 (criterion 7) — the auto per-class attribute growth accrued by
 * `level`, as a `StatBonuses` term for the `World.recomputeStats` merge:
 * `classGrowth[cls][attr] * (level - 1)`. Every field is `0` at level 1, so the
 * merged bonuses — and therefore `effectiveStats` — stay byte-identical to the
 * pre-4.1 result for all six classes (criterion 9).
 */
export function classGrowthBonuses(cls: ClassId, level: number): StatBonuses {
  const g = DATA.progression.classGrowth[cls];
  const n = Math.max(0, Math.floor(level) - 1);
  return {
    strength: g.strength * n,
    agility: g.agility * n,
    energy: g.energy * n,
  };
}
